import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Layout } from "../components/layout";
import { SEO } from "../components/SEO";
import { Calendar, ArrowLeft, Tag } from "lucide-react";
import { sanitizeHTML } from "../utils/sanitize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsPage = () => {
  const [navItems, setNavItems] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [navRes, topicsRes] = await Promise.all([
          axios.get(`${API}/navigation`),
          axios.get(`${API}/topics`),
        ]);
        setNavItems(navRes.data);
        setTopics(topicsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const params = { is_published: true };
        if (selectedTopic) params.topic = selectedTopic;
        const res = await axios.get(`${API}/blogs`, { params });
        setBlogs(res.data);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
      setLoading(false);
    };
    fetchBlogs();
  }, [selectedTopic]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Layout navItems={navItems}>
      <SEO
        title="News & Views"
        description="Stay informed with the latest trends, tips, and news from the world of web development."
        keywords="web development news, website design tips, small business technology, CMS updates"
      />
      {/* Hero */}
      <section data-testid="news-hero" className="section-padding bg-its-teal-dark">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="label-text">News & Views</span>
            <h1 className="heading-1 mt-2 mb-6">
              Insights &
              <span className="text-its-gold"> Updates</span>
            </h1>
            <p className="body-text">
              Stay informed with the latest trends, tips, and news from the world
              of web development.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Topic Filter */}
      <section className="py-8 border-b border-its-teal-light/20">
        <div className="section-container">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-its-offwhite/60 flex items-center gap-2">
              <Tag size={14} />
              Filter by topic:
            </span>
            <button
              onClick={() => setSelectedTopic(null)}
              data-testid="topic-filter-all"
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedTopic === null
                  ? "bg-its-gold text-its-teal"
                  : "bg-its-teal-secondary/30 text-its-offwhite hover:bg-its-teal-secondary/50"
              }`}
            >
              All
            </button>
            {topics.map((topic) => (
              <button
                key={topic.id}
                onClick={() => setSelectedTopic(topic.slug)}
                data-testid={`topic-filter-${topic.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedTopic === topic.slug
                    ? "bg-its-gold text-its-teal"
                    : "bg-its-teal-secondary/30 text-its-offwhite hover:bg-its-teal-secondary/50"
                }`}
              >
                {topic.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-padding">
        <div className="section-container">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-its-offwhite/60">Loading...</div>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-its-offwhite/60">No posts found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogs.map((blog, index) => (
                <motion.article
                  key={blog.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-card group hover:-translate-y-1 transition-all duration-300"
                >
                  {blog.featured_image && (
                    <div className="relative h-48 mb-4 -mx-6 -mt-6 md:-mx-8 md:-mt-8 overflow-hidden rounded-t-xl">
                      <img
                        src={blog.featured_image}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-its-teal-light mb-3">
                    <Calendar size={14} />
                    <time>{formatDate(blog.created_at)}</time>
                  </div>
                  <h2 className="text-xl font-semibold text-white mb-3 group-hover:text-its-gold transition-colors">
                    <Link to={`/news/${blog.slug}`}>{blog.title}</Link>
                  </h2>
                  <p className="text-its-offwhite/80 text-sm line-clamp-3 mb-4">
                    {blog.excerpt}
                  </p>
                  {blog.topics && blog.topics.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {blog.topics.map((topic) => (
                        <span
                          key={topic}
                          className="text-xs px-2 py-1 rounded-full bg-its-teal-light/20 text-its-teal-light"
                        >
                          {topic.replace("-", " ")}
                        </span>
                      ))}
                    </div>
                  )}
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export const BlogDetailPage = () => {
  const { slug } = useParams();
  const [navItems, setNavItems] = useState([]);
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [navRes, blogRes] = await Promise.all([
          axios.get(`${API}/navigation`),
          axios.get(`${API}/blogs/${slug}`),
        ]);
        setNavItems(navRes.data);
        setBlog(blogRes.data);
      } catch (error) {
        console.error("Error fetching blog:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Layout navItems={navItems}>
        <div className="section-padding section-container">
          <div className="animate-pulse text-center">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!blog) {
    return (
      <Layout navItems={navItems}>
        <div className="section-padding section-container text-center">
          <h1 className="heading-2 mb-4">Post Not Found</h1>
          <Link to="/news" className="btn-primary">Back to News</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout navItems={navItems}>
      <SEO
        title={blog.title}
        description={blog.excerpt || blog.title}
        image={blog.featured_image}
        type="article"
        keywords={blog.topics ? blog.topics.join(", ") : "web development, blog"}
      />
      <article data-testid="blog-detail" className="section-padding">
        <div className="section-container max-w-4xl">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-its-teal-light hover:text-its-gold mb-8 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to News & Views
          </Link>

          {blog.featured_image && (
            <img
              src={blog.featured_image}
              alt={blog.title}
              className="w-full h-64 md:h-96 object-cover rounded-xl mb-8"
            />
          )}

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-its-teal-light">
              <Calendar size={16} />
              <time>{formatDate(blog.created_at)}</time>
            </div>
            {blog.topics && blog.topics.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {blog.topics.map((topic) => (
                  <span
                    key={topic}
                    className="text-xs px-2 py-1 rounded-full bg-its-teal-light/20 text-its-teal-light"
                  >
                    {topic.replace("-", " ")}
                  </span>
                ))}
              </div>
            )}
          </div>

          <h1 className="heading-1 mb-8">{blog.title}</h1>

          <div
            className="prose prose-invert max-w-none text-its-offwhite/90"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(blog.content) }}
          />
        </div>
      </article>
    </Layout>
  );
};

export default NewsPage;
